import { useState } from "react";
import { ChevronDown, ExternalLink, LogOut, Settings } from "lucide-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { isMigratedPath } from "@/route-manifest";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { useAuth } from "@/contexts/AuthContext";
import { useThemeMode } from "@/contexts/ThemeContext";
import { LegacyLink } from "./LegacyLink";
import { SidebarLogo } from "./SidebarLogo";
import { useNavSections } from "./nav-items";
import type { NavItem } from "./nav-items";

const DOCUMENTATION_URL = "https://docs.bunyan.ai";

const NavLink = ({ item, collapsed }: { item: NavItem; collapsed: boolean }) => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Case-insensitive because route matching is: both routers resolve
  // /bluePrints to /blueprints, so the highlight has to agree with them.
  const active = pathname.toLowerCase() === item.to.toLowerCase();
  const Icon = item.icon;

  const className = cn(
    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
    "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
    active && "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
    collapsed && "justify-center px-2",
  );

  const body = (
    <>
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </>
  );

  const title = collapsed ? item.label : undefined;

  // A migrated screen gets a real typed <Link>, checked against the generated
  // route tree. LegacyLink would still navigate correctly — it pushes the same
  // URL and the router re-matches it — but it would tell us nothing about
  // whether the route exists.
  return isMigratedPath(item.to) ? (
    <Link to={item.to} title={title} className={className}>
      {body}
    </Link>
  ) : (
    <LegacyLink to={item.to} title={title} className={className}>
      {body}
    </LegacyLink>
  );
};

const SidebarSection = ({
  label,
  collapsible,
  collapsed,
  children,
}: {
  label?: string;
  collapsible: boolean;
  collapsed: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(true);

  if (!label || collapsed) return <div className="space-y-0.5">{children}</div>;

  return (
    <div className="space-y-0.5">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center justify-between rounded-md px-3 py-1.5 text-xs text-sidebar-foreground/60 hover:text-sidebar-foreground"
        >
          <span>{label}</span>
          <ChevronDown
            className={cn("size-3.5 transition-transform", !open && "-rotate-90")}
            aria-hidden="true"
          />
        </button>
      ) : (
        <div className="px-3 py-1.5 text-xs text-sidebar-foreground/60">
          {label}
        </div>
      )}
      {open && <div className="space-y-0.5">{children}</div>}
    </div>
  );
};

export const AppSidebar = ({ collapsed }: { collapsed: boolean }) => {
  const { t } = useTranslation();
  const sections = useNavSections();
  const { currentUser, userProfile, logOut } = useAuth();
  const { mode, toggleTheme } = useThemeMode();

  const displayName =
    userProfile?.name || userProfile?.username || currentUser?.email || "";

  return (
    <aside
      className={cn(
        "flex h-full flex-col bg-sidebar text-sidebar-foreground transition-[width]",
        collapsed ? "w-16" : "w-64",
      )}
    >
      <div
        className={cn(
          "flex h-16 shrink-0 items-center px-4",
          collapsed && "justify-center px-2",
        )}
      >
        <Link to="/" className="flex items-center overflow-hidden" aria-label="Bunyan">
          <SidebarLogo collapsed={collapsed} />
        </Link>
      </div>

      <nav
        aria-label={t("sidebar.knowledgeBase")}
        className="flex-1 space-y-4 overflow-y-auto px-2 py-2"
      >
        {sections.map((section) => (
          <SidebarSection
            key={section.key}
            label={section.label}
            collapsible={section.collapsible}
            collapsed={collapsed}
          >
            {section.items.map((item) => (
              <NavLink key={item.key} item={item} collapsed={collapsed} />
            ))}
          </SidebarSection>
        ))}
      </nav>

      <div className="shrink-0 space-y-0.5 p-2">
        <button
          type="button"
          onClick={toggleTheme}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            collapsed && "justify-center px-2",
          )}
        >
          <span className="size-4 shrink-0 text-center leading-4" aria-hidden="true">
            {mode === "dark" ? "☾" : "☀"}
          </span>
          {!collapsed && <span>{t("sidebar.darkTheme")}</span>}
        </button>

        <LegacyLink
          to="/settings"
          className={cn(
            "flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            collapsed && "justify-center px-2",
          )}
        >
          <Settings className="size-4 shrink-0" aria-hidden="true" />
          {!collapsed && <span>{t("sidebar.settings")}</span>}
        </LegacyLink>

        <a
          href={DOCUMENTATION_URL}
          target="_blank"
          rel="noreferrer"
          className={cn(
            "flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            collapsed && "justify-center px-2",
          )}
        >
          <ExternalLink className="size-4 shrink-0" aria-hidden="true" />
          {!collapsed && <span>{t("sidebar.documentation")}</span>}
        </a>

        {displayName && (
          <div
            className={cn(
              "mt-1 flex items-center gap-2 rounded-md px-3 py-2",
              collapsed && "justify-center px-2",
            )}
          >
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-sidebar-primary text-xs text-sidebar-primary-foreground">
              {displayName.charAt(0).toUpperCase()}
            </span>
            {!collapsed && (
              <>
                <span className="min-w-0 flex-1 truncate text-sm">
                  {displayName}
                </span>
                <button
                  type="button"
                  onClick={logOut}
                  aria-label={t("common.logout")}
                  className="rounded p-1 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                >
                  <LogOut className="size-4" aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};
